/**
 * Boli Live - KYC Verification Page
 *
 * Page for users to submit their CNIC for identity verification.
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Upload,
  Camera,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Loader2,
  CreditCard,
  User,
  Calendar,
} from 'lucide-react';
import { useAuthStore } from '../lib/stores/auth-store';
import { getKYCService } from '@boli/core';
import type { KYCStatusResponse, KYCTier } from '@boli/types';

export default function KYCVerificationPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized } = useAuthStore();
  const [kycStatus, setKycStatus] = useState<KYCStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form state
  const [cnicNumber, setCnicNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [requestedTier, setRequestedTier] = useState<KYCTier>('tier1');
  const [cnicFront, setCnicFront] = useState<File | null>(null);
  const [cnicBack, setCnicBack] = useState<File | null>(null);
  const [cnicFrontPreview, setCnicFrontPreview] = useState<string | null>(null);
  const [cnicBackPreview, setCnicBackPreview] = useState<string | null>(null);

  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate('/profile');
    }
  }, [isInitialized, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchKYCStatus();
    }
  }, [isAuthenticated]);

  const fetchKYCStatus = async () => {
    try {
      const kycService = getKYCService();
      const status = await kycService.getKYCStatus();
      setKycStatus(status);
    } catch {
      // Failed to fetch KYC status
    } finally {
      setIsLoading(false);
    }
  };

  const formatCNIC = (value: string) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XXXXX-XXXXXXX-X
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12, 13)}`;
  };

  const handleCNICChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNIC(e.target.value);
    if (formatted.length <= 15) { // XXXXX-XXXXXXX-X = 15 chars
      setCnicNumber(formatted);
    }
  };

  const handleFileChange = (type: 'front' | 'back', file: File | null) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'front') {
        setCnicFront(file);
        setCnicFrontPreview(e.target?.result as string);
      } else {
        setCnicBack(file);
        setCnicBackPreview(e.target?.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!cnicNumber || cnicNumber.length !== 15) {
      setError('Please enter a valid CNIC number');
      return;
    }
    if (!fullName || fullName.trim().length < 3) {
      setError('Please enter your full name');
      return;
    }
    if (!dateOfBirth) {
      setError('Please enter your date of birth');
      return;
    }
    if (!cnicFront || !cnicBack) {
      setError('Please upload both front and back of your CNIC');
      return;
    }

    setIsSubmitting(true);

    try {
      const kycService = getKYCService();
      const result = await kycService.submitKYC({
        cnicNumber,
        fullName: fullName.trim(),
        dateOfBirth,
        requestedTier,
        cnicFrontFile: cnicFront,
        cnicBackFile: cnicBack,
      });

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          fetchKYCStatus();
        }, 2000);
      } else {
        setError(result.error?.message || 'Failed to submit KYC');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show status if already submitted
  if (kycStatus?.hasSubmission && !success) {
    const submission = kycStatus.submission!;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-lg mx-auto px-4 py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <Link
                to="/profile"
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-foreground">KYC Verification</h1>
                <p className="text-sm text-muted-foreground">Identity verification status</p>
              </div>
            </div>

            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-border p-6 mb-6">
              <div className="flex items-center gap-4 mb-6">
                {submission.status === 'approved' ? (
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                ) : submission.status === 'pending' ? (
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="w-8 h-8 text-yellow-600" />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {submission.status === 'approved'
                      ? 'Verified'
                      : submission.status === 'pending'
                      ? 'Under Review'
                      : 'Rejected'}
                  </h2>
                  <p className="text-muted-foreground">
                    {submission.status === 'approved'
                      ? `You're verified at ${submission.requestedTier === 'tier2' ? 'Tier 2' : 'Tier 1'}`
                      : submission.status === 'pending'
                      ? 'We\'re reviewing your documents'
                      : 'Your submission was rejected'}
                  </p>
                </div>
              </div>

              {submission.status === 'rejected' && submission.rejectionReason && (
                <div className="p-4 bg-red-50 rounded-lg mb-6">
                  <p className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</p>
                  <p className="text-sm text-red-700">{submission.rejectionReason}</p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Tier</span>
                  <span className="font-medium">
                    {kycStatus.currentTier === 'tier2'
                      ? 'Tier 2 (Verified+)'
                      : kycStatus.currentTier === 'tier1'
                      ? 'Tier 1 (Basic)'
                      : 'Unverified'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Max Bid Limit</span>
                  <span className="font-medium">{formatCurrency(kycStatus.bidLimit)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Submitted</span>
                  <span className="font-medium">
                    {new Date(submission.createdAt).toLocaleDateString('en-PK')}
                  </span>
                </div>
              </div>
            </div>

            {/* Resubmit if rejected */}
            {submission.status === 'rejected' && (
              <button
                onClick={() => {
                  setKycStatus(null);
                  setSuccess(false);
                }}
                className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                Submit New Application
              </button>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-lg mx-auto px-4 py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Submitted Successfully!</h1>
            <p className="text-muted-foreground mb-8">
              We'll review your documents within 24-48 hours. You'll be notified once verified.
            </p>
            <Link
              to="/profile"
              className="inline-flex px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              Back to Profile
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show submission form
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-lg mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/profile"
              className="p-2 hover:bg-secondary rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Verify Your Identity</h1>
              <p className="text-sm text-muted-foreground">Submit your CNIC for verification</p>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-medium text-foreground">Benefits of Verification</span>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Tier 1: Bid up to {formatCurrency(50000)}</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Tier 2: Bid up to {formatCurrency(500000)}</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Verified badge on your profile</span>
              </li>
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* CNIC Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <CreditCard className="w-4 h-4 inline mr-2" />
                CNIC Number
              </label>
              <input
                type="text"
                value={cnicNumber}
                onChange={handleCNICChange}
                placeholder="XXXXX-XXXXXXX-X"
                className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Full Name (as on CNIC)
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Date of Birth
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Tier Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Verification Tier
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRequestedTier('tier1')}
                  className={`p-4 border rounded-xl text-left transition-colors ${
                    requestedTier === 'tier1'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-secondary/50'
                  }`}
                >
                  <p className="font-medium">Tier 1</p>
                  <p className="text-sm text-muted-foreground">Up to {formatCurrency(50000)}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setRequestedTier('tier2')}
                  className={`p-4 border rounded-xl text-left transition-colors ${
                    requestedTier === 'tier2'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-secondary/50'
                  }`}
                >
                  <p className="font-medium">Tier 2</p>
                  <p className="text-sm text-muted-foreground">Up to {formatCurrency(500000)}</p>
                </button>
              </div>
            </div>

            {/* CNIC Upload - Front */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CNIC Front Side
              </label>
              <input
                ref={frontInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange('front', e.target.files?.[0] || null)}
                className="hidden"
              />
              {cnicFrontPreview ? (
                <div className="relative">
                  <img
                    src={cnicFrontPreview}
                    alt="CNIC Front"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => frontInputRef.current?.click()}
                    className="absolute bottom-2 right-2 px-3 py-1 bg-black/50 text-white text-sm rounded-lg"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => frontInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-secondary/50 transition-colors"
                >
                  <div className="p-3 bg-secondary rounded-full">
                    <Camera className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Upload front of CNIC
                  </span>
                </button>
              )}
            </div>

            {/* CNIC Upload - Back */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CNIC Back Side
              </label>
              <input
                ref={backInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange('back', e.target.files?.[0] || null)}
                className="hidden"
              />
              {cnicBackPreview ? (
                <div className="relative">
                  <img
                    src={cnicBackPreview}
                    alt="CNIC Back"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => backInputRef.current?.click()}
                    className="absolute bottom-2 right-2 px-3 py-1 bg-black/50 text-white text-sm rounded-lg"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => backInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:bg-secondary/50 transition-colors"
                >
                  <div className="p-3 bg-secondary rounded-full">
                    <Camera className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Upload back of CNIC
                  </span>
                </button>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Submit for Verification
                </>
              )}
            </button>
          </form>

          {/* Privacy note */}
          <p className="text-xs text-muted-foreground text-center mt-6">
            Your documents are securely stored and only used for identity verification.
            We comply with all data protection regulations.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
